import {
  $applyNodeReplacement,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

export type SerializedActivityMentionNode = Spread<
  {
    mentionId: number;
    mentionLabel: string;
    type: 'activity-mention';
    version: 1;
  },
  SerializedTextNode
>;

export class ActivityLexicalMentionNode extends TextNode {
  __mentionId: number;
  __mentionLabel: string;

  static getType(): string {
    return 'activity-mention';
  }

  static clone(node: ActivityLexicalMentionNode): ActivityLexicalMentionNode {
    return new ActivityLexicalMentionNode(
      node.__mentionId,
      node.__mentionLabel,
      node.__text,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedActivityMentionNode): ActivityLexicalMentionNode {
    return $createActivityLexicalMentionNode(
      serializedNode.mentionId,
      serializedNode.mentionLabel
    ).updateFromJSON(serializedNode);
  }

  constructor(mentionId: number, mentionLabel: string, text?: string, key?: NodeKey) {
    super(text ?? mentionLabel, key);
    this.__mentionId = mentionId;
    this.__mentionLabel = mentionLabel;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.dataset.mentionId = String(this.__mentionId);
    dom.dataset.mentionLabel = this.__mentionLabel;
    dom.style.color = 'var(--mantine-color-blue-7)';
    dom.style.background = 'var(--mantine-color-blue-0)';
    dom.style.borderRadius = '6px';
    dom.style.padding = '0 2px';
    dom.style.boxDecorationBreak = 'clone';
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config);
    if (
      prevNode.__mentionId !== this.__mentionId ||
      prevNode.__mentionLabel !== this.__mentionLabel
    ) {
      dom.dataset.mentionId = String(this.__mentionId);
      dom.dataset.mentionLabel = this.__mentionLabel;
    }
    return updated;
  }

  exportJSON(): SerializedActivityMentionNode {
    return {
      ...super.exportJSON(),
      mentionId: this.__mentionId,
      mentionLabel: this.__mentionLabel,
      type: 'activity-mention',
      version: 1,
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): false {
    return false;
  }

  canInsertTextAfter(): false {
    return false;
  }

  getMentionId(): number {
    return this.getLatest().__mentionId;
  }

  getMentionLabel(): string {
    return this.getLatest().__mentionLabel;
  }
}

export function $createActivityLexicalMentionNode(
  mentionId: number,
  mentionLabel: string
): ActivityLexicalMentionNode {
  return $applyNodeReplacement(new ActivityLexicalMentionNode(mentionId, mentionLabel));
}

export function $isActivityLexicalMentionNode(
  node: LexicalNode | null | undefined
): node is ActivityLexicalMentionNode {
  return node instanceof ActivityLexicalMentionNode;
}
